/**
 * Settings → Email Notifications
 *
 * Admin-only template list and editor for external email notification templates.
 */
import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { notify } from '@/lib/notify';
import {
  IconAlertCircle,
  IconArchive,
  IconDotsVertical,
  IconMail,
  IconPlus,
} from '@tabler/icons-react';
import type { UnifyTableColumn } from '@/components/UnifyTable';
import { UnifyTable } from '@/components/UnifyTable';
import { useEffectiveDisplayUserRole } from '@/lib/displayUserRole';

import type {
  NotificationTemplateDraft,
  NotificationTemplateListItem,
} from './notification-template.types';
import { TEMPLATE_STATUS_OPTIONS } from './notification-template.constants';
import { activityDefinitionToListItem, activityDefinitionToDraft } from './notification-template.medplum';
import { useNotificationTemplates } from './hooks/useNotificationTemplates';
import { NotificationTemplateEditor } from './components/NotificationTemplateEditor';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

type View =
  | { kind: 'list' }
  | { kind: 'editor'; draft: NotificationTemplateDraft };

export function NotificationTemplatesPage(): JSX.Element {
  const userRole = useEffectiveDisplayUserRole();

  // Defense-in-depth: SettingsPage sidebar + redirect already gate this,
  // but guard here too so direct URL access cannot render admin UI.
  if (userRole !== 'Unify Admin') {
    return <Navigate to="/dashboard/settings/profile" replace />;
  }

  return <NotificationTemplatesContent />;
}

// Hooks live in their own component so the role guard above cannot make them conditional
// (and non-admins still never load templates).
function NotificationTemplatesContent(): JSX.Element {
  const engine = useNotificationTemplates();
  const [view, setView] = useState<View>({ kind: 'list' });
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<NotificationTemplateListItem | null>(null);

  const listItems: NotificationTemplateListItem[] = useMemo(
    () => engine.templates.map(activityDefinitionToListItem),
    [engine.templates],
  );

  const handleCreate = () => {
    const draft: NotificationTemplateDraft = {
      name: '',
      description: '',
      status: 'draft',
      version: '1',
      channel: 'email',
      from: { mode: 'static', email: '', name: '' },
      subject: '',
      htmlBody: '',
      textBody: '',
      variables: [],
    };
    setView({ kind: 'editor', draft });
  };

  const handleEdit = useCallback(
    (item: NotificationTemplateListItem) => {
      const draft = engine.getDraft(item.id);
      if (draft) {
        setView({ kind: 'editor', draft });
      }
    },
    [engine],
  );

  const handleBack = () => setView({ kind: 'list' });

  const handleSave = async (draft: NotificationTemplateDraft): Promise<NotificationTemplateDraft> => {
    setSaving(true);
    try {
      const saved = await engine.save(draft);
      // Use the ActivityDefinition returned from the server — `getDraft` can lag React state
      // after `reload`, and the parent's `view.draft` for "new template" was never synced with
      // editor-local edits; rebuilding from `saved` keeps the editor populated after save.
      const updatedDraft = activityDefinitionToDraft(saved);
      setView({ kind: 'editor', draft: updatedDraft });
      notify({ title: 'Saved', message: 'Template saved successfully.', type: 'success' });
      return updatedDraft;
    } catch (err) {
      notify({
        title: 'Error',
        error: err,
        message: 'Failed to save template.',
        type: 'error',
      });
      return draft;
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await engine.archive(archiveTarget.id);
      setArchiveTarget(null);
      notify({ title: 'Archived', message: 'Template has been archived.', type: 'success' });
    } catch {
      notify({ title: 'Error', message: 'Could not archive template.', type: 'error' });
    }
  };

  if (engine.loading) {
    return (
      <Container size="lg" py="xl">
        <Center py="xl"><Loader size="lg" /></Center>
      </Container>
    );
  }

  if (engine.error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Could not load templates" icon={<IconAlertCircle size={16} />}>
          {getUserFacingErrorMessage(engine.error)}
        </Alert>
      </Container>
    );
  }

  if (view.kind === 'editor') {
    return (
      <NotificationTemplateEditor
        key={view.draft.id ?? 'new-template'}
        initial={view.draft}
        onSave={handleSave}
        onBack={handleBack}
        saving={saving}
      />
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <div>
            <Title order={2}>Email Notifications</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Manage email notification templates, variables, sender config, and preview.
            </Text>
          </div>
          <Button color="gold" leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            New template
          </Button>
        </Group>

        <Group gap="md" wrap="wrap">
          <StatBadge label="Active" count={listItems.filter((i) => i.status === 'active').length} color="green" />
          <StatBadge label="Draft" count={listItems.filter((i) => i.status === 'draft').length} color="yellow" />
          <StatBadge label="Archived" count={listItems.filter((i) => i.status === 'archived').length} color="gray" />
        </Group>

        {listItems.length === 0 ? (
          <Paper withBorder p="xl" radius="md">
            <Stack align="center" gap="md">
              <ThemeIcon size={72} radius="xl" color="gray" variant="light">
                <IconMail size={36} />
              </ThemeIcon>
              <Title order={3}>No notification templates yet</Title>
              <Text size="sm" c="gray.6" maw={460} ta="center">
                Email notification templates define the content, variables, and sender config
                for outbound emails. Create your first template to get started.
              </Text>
              <Button leftSection={<IconPlus size={16} />} color="gold" onClick={handleCreate}>
                New template
              </Button>
            </Stack>
          </Paper>
        ) : (
          <NotificationTemplateTable
            items={listItems}
            onRowClick={handleEdit}
            onArchive={(item) => setArchiveTarget(item)}
          />
        )}
      </Stack>

      <Modal
        opened={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        title="Archive template"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to archive <b>{archiveTarget?.name}</b>? Archived templates
            will no longer be available for use.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setArchiveTarget(null)}>Cancel</Button>
            <Button color="red" onClick={handleArchive}>Archive</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function NotificationTemplateTable({
  items,
  onRowClick,
  onArchive,
}: {
  items: NotificationTemplateListItem[];
  onRowClick: (item: NotificationTemplateListItem) => void;
  onArchive: (item: NotificationTemplateListItem) => void;
}): JSX.Element {
  const columns: UnifyTableColumn<NotificationTemplateListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        getValue: (row) => row.name,
        render: (row) => (
          <Text size="sm" fw={500}>{row.name}</Text>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        getValue: (row) => row.status,
        render: (row) => {
          const opt = TEMPLATE_STATUS_OPTIONS.find((o) => o.value === row.status);
          return (
            <Badge color={opt?.color ?? 'gray'} variant="light" size="sm">
              {opt?.label ?? row.status}
            </Badge>
          );
        },
      },
      {
        key: 'subject',
        label: 'Subject',
        getValue: (row) => row.subject,
        render: (row) => (
          <Text size="sm" c="dimmed" lineClamp={1}>{row.subject || '—'}</Text>
        ),
      },
      {
        key: 'updatedAt',
        label: 'Updated',
        width: 140,
        getValue: (row) => row.updatedAt,
        render: (row) => (
          <Text size="xs" c="dimmed">
            {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—'}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: 48,
        pinRight: true,
        getValue: () => '',
        render: (row) => (
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                p={4}
                onClick={(e) => e.stopPropagation()}
              >
                <IconDotsVertical size={14} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
              {row.status !== 'archived' && (
                <Menu.Item
                  leftSection={<IconArchive size={14} />}
                  onClick={() => onArchive(row)}
                >
                  Archive
                </Menu.Item>
              )}
              {row.status === 'archived' && (
                <Menu.Item disabled leftSection={<IconArchive size={14} />}>
                  Already archived
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ),
      },
    ],
    [onArchive],
  );

  return (
    <UnifyTable
      columns={columns}
      data={items}
      getRowKey={(row) => row.id}
      searchable
      searchPlaceholder="Search templates"
      sortable
      pagination
      defaultRowsPerPage={25}
      onRowClick={onRowClick}
      tableProps={{ highlightOnHover: true }}
      filter={{
        id: 'statusFilter',
        placeholder: 'Status',
        defaultValue: 'active',
        columnKey: 'status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'draft', label: 'Draft' },
          { value: 'archived', label: 'Archived' },
        ],
      }}
    />
  );
}

function StatBadge({ label, count, color }: { label: string; count: number; color: string }): JSX.Element {
  return (
    <Paper withBorder px="md" py="xs" radius="md">
      <Group gap="xs">
        <Badge
          color={color}
          variant="filled"
          size="sm"
          radius="xl"
          miw={28}
          px={6}
          styles={{ label: { overflow: 'visible', textOverflow: 'unset' } }}
        >
          {count}
        </Badge>
        <Text size="sm" c="dimmed">{label}</Text>
      </Group>
    </Paper>
  );
}
